import React, { useState, useEffect, useMemo, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { cn } from '../lib/utils';
import { useFreezeBackground } from '../hooks/useFreezeBackground';
import { fetchTopicDetail, getCachedTopicDetail } from '../lib/contentCache';

const TOPIC_IMAGE_FRAME_CLASS =
  'mx-auto w-full max-w-5xl overflow-hidden rounded-[2rem] bg-white/45 shadow-[0_20px_60px_rgba(15,23,42,0.18),0_8px_24px_rgba(15,23,42,0.08)] ring-1 ring-black/5 backdrop-blur-[2px] dark:bg-slate-900/35 dark:ring-white/10';
const TOPIC_IMAGE_CLASS =
  'block h-[18rem] w-full rounded-[2rem] object-cover shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] sm:h-[22rem] lg:h-[26rem]';
const BACK_TRANSITION_MS = 360;

const TopicPage = ({ isOverlay = false }) => {
  const { themeName } = useContext(ThemeContext);
  const { topicId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const cachedTopic = getCachedTopicDetail(topicId);
  const [topic, setTopic] = useState(() => cachedTopic);
  const [loading, setLoading] = useState(() => !cachedTopic);
  const [error, setError] = useState(null);
  const [isLeaving, setIsLeaving] = useState(false);
  const [isVisible, setIsVisible] = useState(() => !isOverlay);
  const leaveTimeoutRef = useRef(0);

  useFreezeBackground(isOverlay);

  useEffect(() => {
    let isMounted = true;
    const nextCachedTopic = getCachedTopicDetail(topicId);

    setTopic(nextCachedTopic);
    setLoading(!nextCachedTopic);
    setError(null);

    fetchTopicDetail(topicId)
      .then((nextTopic) => {
        if (!isMounted) return;
        setTopic(nextTopic);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError('Unable to load topic details.');
        console.error(err);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [topicId]);

  useEffect(() => {
    if (!isOverlay) {
      setIsVisible(true);
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      setIsVisible(true);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isOverlay, topicId]);

  useEffect(() => {
    return () => {
      if (leaveTimeoutRef.current) {
        window.clearTimeout(leaveTimeoutRef.current);
        leaveTimeoutRef.current = 0;
      }
    };
  }, []);

  const closeTopicPage = useCallback(() => {
    if (isLeaving) return;

    if (isOverlay) {
      setIsLeaving(true);
      setIsVisible(false);
      leaveTimeoutRef.current = window.setTimeout(() => {
        if (location.state?.backgroundLocation) {
          navigate(-1);
          return;
        }

        navigate('/', { replace: true });
      }, BACK_TRANSITION_MS);
      return;
    }

    navigate(-1);
  }, [isLeaving, isOverlay, location.state, navigate]);

  const renderBlock = (block) => {
    switch (block.type) {
      case 'header': {
        const Tag = `h${block.data.level}`;
        return <Tag key={block.id} dangerouslySetInnerHTML={{ __html: block.data.text }} />;
      }
      case 'paragraph':
        return <p key={block.id} dangerouslySetInnerHTML={{ __html: block.data.text }} />;
      case 'list': {
        const ListTag = block.data.style === 'ordered' ? 'ol' : 'ul';
        return (
          <ListTag key={block.id}>
            {block.data.items.map((item, index) => (
              <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ListTag>
        );
      }
      case 'image':
        return (
          <figure key={block.id} className="not-prose my-12">
            <div className={TOPIC_IMAGE_FRAME_CLASS}>
              <img className={TOPIC_IMAGE_CLASS} src={block.data.file.url} alt={block.data.caption || ''} />
            </div>
            {block.data.caption && (
              <figcaption className="px-2 pt-4 text-center text-sm leading-6 text-muted-foreground">
                {block.data.caption}
              </figcaption>
            )}
          </figure>
        );
      default:
        return null;
    }
  };

  const { heroBlock, contentBlocks } = useMemo(() => {
    if (!topic?.content?.blocks) {
      return { heroBlock: null, contentBlocks: [] };
    }
    const blocks = topic.content.blocks;
    if (blocks[0]?.type === 'image') {
      return {
        heroBlock: blocks[0],
        contentBlocks: blocks.slice(1),
      };
    }
    return { heroBlock: null, contentBlocks: blocks };
  }, [topic]);

  const proseClass = themeName === 'dark' ? 'prose-invert' : '';
  const bodyClassName = cn(
    'mx-auto w-full max-w-6xl px-6 pb-24 pt-16 transition-[opacity,transform] duration-300 ease-out will-change-[opacity,transform] motion-reduce:transition-none',
    isLeaving ? 'translate-y-2 opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'
  );

  const content = loading ? (
    <div className="grid min-h-[60vh] place-items-center px-6">
      <h1 className="text-xl font-semibold">Loading...</h1>
    </div>
  ) : error ? (
    <div className="grid min-h-[60vh] place-items-center px-6">
      <h1 className="text-xl font-semibold">{error}</h1>
    </div>
  ) : (
    <div className={bodyClassName}>
      <Button
        variant="ghost"
        className="mb-6 gap-2 text-sm"
        onClick={closeTopicPage}
        title="Back"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
        <span>Back</span>
      </Button>

      {heroBlock && (
        <section className="mb-10">
          <div className={TOPIC_IMAGE_FRAME_CLASS}>
            <img
              className={TOPIC_IMAGE_CLASS}
              src={heroBlock.data.file.url}
              alt={heroBlock.data.caption || topic.title}
            />
          </div>

          <div className="mx-auto mt-6 max-w-4xl text-center">
            <span className="text-xs font-semibold uppercase tracking-[0.28em] text-muted-foreground">
              Explore Topic
            </span>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">{topic.title}</h1>
            {heroBlock.data.caption && (
              <p className="mt-4 text-base leading-7 text-muted-foreground sm:text-lg">
                {heroBlock.data.caption}
              </p>
            )}
          </div>
        </section>
      )}

      <Card className="glass-panel mx-auto">
        <CardContent className="p-8 sm:p-12">
          {!heroBlock && <h1 className="mb-6 text-3xl font-semibold">{topic.title}</h1>}
          <div
            className={cn(
              'prose prose-slate max-w-none text-foreground [&_figure]:mx-auto [&_figure]:my-12 [&_figure]:w-full [&_figure]:max-w-5xl [&_figure]:overflow-hidden [&_figure]:rounded-[2rem] [&_figure]:bg-white/45 [&_figure]:shadow-[0_20px_60px_rgba(15,23,42,0.18),0_8px_24px_rgba(15,23,42,0.08)] [&_figure]:ring-1 [&_figure]:ring-black/5 [&_figure]:backdrop-blur-[2px] [&_img]:block [&_img]:h-[18rem] [&_img]:w-full [&_img]:rounded-[2rem] [&_img]:object-cover [&_img]:shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] [&_picture]:block [&_picture]:overflow-hidden [&_picture]:rounded-[2rem] [&_picture]:bg-white/45 [&_picture]:shadow-[0_20px_60px_rgba(15,23,42,0.18),0_8px_24px_rgba(15,23,42,0.08)] [&_picture]:ring-1 [&_picture]:ring-black/5 [&_picture]:backdrop-blur-[2px] dark:[&_figure]:bg-slate-900/35 dark:[&_figure]:ring-white/10 dark:[&_picture]:bg-slate-900/35 dark:[&_picture]:ring-white/10 sm:[&_img]:h-[22rem] lg:[&_img]:h-[26rem]',
              proseClass
            )}
          >
            {contentBlocks.length > 0 ? contentBlocks.map(renderBlock) : <p>No content available.</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className={isOverlay ? 'fixed inset-0 z-50' : 'w-full'}>
      {isOverlay ? (
        <button
          type="button"
          className="detail-dialog-overlay fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          data-state={isVisible ? 'open' : 'closed'}
          aria-label="Close topic details"
          onClick={closeTopicPage}
        />
      ) : null}
      {isOverlay ? (
        <div
          className="detail-dialog detail-dialog--comparison fixed left-[50%] z-50 w-[min(96vw,1240px)] max-w-none overflow-hidden rounded-lg border border-border/60 bg-background/95 p-0 shadow-lg backdrop-blur-xl focus:outline-none focus-visible:outline-none h-[88vh] flex flex-col"
          data-state={isVisible ? 'open' : 'closed'}
        >
          <div className="detail-content-shell h-full overflow-y-auto">{content}</div>
        </div>
      ) : (
        <div>{content}</div>
      )}
    </div>
  );
};

export default TopicPage;
